"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type TimelineEntry = {
  year: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const entries: TimelineEntry[] = [{
  year: "2018",
  title: "The Underground Genesis",
  description: "Drip District erupts from the urban core with a rebellious ethos: bridging raw street culture with uncompromising modern luxury craftsmanship. Built for those who dictate the culture.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_founding_atelier.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_founding_atelier.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_founding_atelier.png-wvc-sizes",
  alt: "Minimalist fashion design studio with fabric swatches"
}, {
  year: "2020",
  title: "The Techwear Blueprint",
  description: "Launching our first technical outerwear capsule, Drip District redefines modern streetwear with weather-proof tactical silhouettes and avant-garde hardware detailing.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_signature_collection.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_signature_collection.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_signature_collection.png-wvc-srcset",
  alt: "Model wearing signature minimalist fashion piece"
}, {
  year: "2021",
  title: "Global Street Syndicate",
  description: "Expanding into international cultural hubs, our collaborative drops sell out within minutes, cementing Drip District as a vanguard of contemporary luxury streetwear.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_urban_series.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_urban_series.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_urban_series.png-wvc-sizes",
  alt: "Urban series streetwear on a concrete background"
}, {
  year: "2022",
  title: "Monolith: Noir Edition",
  description: "An all-black monochromatic collection engineered for nocturnal city dwellers, focusing on oversized drape, heavy cotton fleece, and stealth utility pockets.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_noir_evening.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_noir_evening.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_noir_evening.webp-wvc-sizes",
  alt: "Noir evening gown on a dark architectural background"
}, {
  year: "2023",
  title: "The Limited Drop Protocol",
  description: "Moving to strict micro-batch releases of hand-distressed outerwear and numbered collector garments to guarantee absolute exclusivity and zero deadstock.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_atelier_limited.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_atelier_limited.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_atelier_limited.png-wvc-sizes",
  alt: "Limited edition garment tag and stitching detail"
}, {
  year: "2024",
  title: "The Vanguard Retrospective",
  description: "Six years of defying convention. A celebration of defiant streetwear aesthetics, radical silhouettes, and a relentless commitment to future-proof luxury design.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_decade_retrospective.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_decade_retrospective.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_decade_retrospective.png-wvc-sizes",
  alt: "Retrospective display of Drip District garments"
}];
function TimelineEntryItem({
  entry,
  index
}: {
  entry: TimelineEntry;
  index: number;
}) {
  const isLeft = index % 2 === 0;
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2,
    rootMargin: "0px 0px -80px 0px"
  });
  return <div ref={ref} data-index={index} className="relative md:grid md:grid-cols-2 md:gap-0 pl-12 md:pl-0">
      {}
      <div className={`absolute top-2 md:top-3 left-0 md:left-1/2 md:-translate-x-1/2 w-3 h-3 transition-all duration-500 z-10 ${isVisible ? "bg-accent shadow-lg scale-110" : "bg-muted-foreground/40"}`} aria-hidden="true" />

      {}
      <div className={`hidden md:block absolute top-4 h-[2px] bg-border/60 ${isLeft ? "right-1/2 mr-3 w-[calc(50%-4rem)]" : "left-1/2 ml-3 w-[calc(50%-4rem)]"}`} aria-hidden="true" />
      <div className={`md:hidden absolute top-4 left-2 w-8 h-[2px] bg-border/60`} aria-hidden="true" />

      {}
      <div className={`${isLeft ? "md:pr-16 md:text-right md:col-start-1" : "md:col-start-2 md:pl-16 md:order-2"} ${isLeft ? `transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 md:-translate-x-8 -translate-x-4"}` : `transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 md:translate-x-8 -translate-x-4"}`}`}>
        <div className="font-mono text-sm font-bold tracking-[0.2em] text-accent mb-3">
          {entry.year}
        </div>
        <h3 className="font-default font-bold text-3xl md:text-4xl leading-[1.1] tracking-tighter text-foreground mb-4">
          {entry.title}
        </h3>
        <p className="font-default text-base md:text-lg leading-relaxed tracking-wide text-muted-foreground max-w-md md:max-w-none md:inline-block">
          {entry.description}
        </p>

        {}
        <div className={`mt-8 ${isLeft ? "md:ml-auto" : ""} w-full md:w-64 max-w-[260px]`}>
          <div className="relative aspect-[3/4] overflow-hidden shadow-xl">
            <img src={entry.image} data-wvc-srcset={entry.srcset} data-wvc-sizes={entry.sizes} alt={entry.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
          </div>
        </div>
      </div>

      {}
      <div className={`hidden md:block ${isLeft ? "md:col-start-2" : "md:col-start-1 md:row-start-1"}`} aria-hidden="true" />
    </div>;
}
export default function Milestones() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4526" id="fashion-milestones" className="bg-background text-foreground py-24 md:py-40 px-6 md:px-10 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-20 md:mb-32 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <div className="font-mono text-xs tracking-widest text-accent mb-6 md:mb-8">
              HERITAGE / 02
            </div>
            <h2 className="font-default font-bold text-4xl md:text-6xl lg:text-[4.5rem] leading-[0.95] tracking-tight text-foreground">
              Six years.
              <br />
              A legacy of
              <br />
              <span className="font-default italic font-medium">
                permanence.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground">
              From an underground vision to a global vanguard of modern luxury
              streetwear, the story of Drip District is defined by relentless
              innovation, bold silhouettes, and garments engineered to shape the
              future of contemporary culture.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute top-0 bottom-0 w-[2px] bg-border/40 left-[6px] md:left-1/2 md:-translate-x-1/2" aria-hidden="true" />

          <div className="space-y-24 md:space-y-[160px]">
            {entries.map((entry, i) => <TimelineEntryItem entry={entry} index={i} key={i} />)}
          </div>
        </div>
      </div>
    </section>;
}